import { useCallback, useEffect, useRef, useState } from "react";

const OVERLAY_DURATION_MS = 3000;

const isScreenshotShortcut = (event) => {
  if (event.key === "PrintScreen" || event.code === "PrintScreen") {
    return true;
  }
  if (event.metaKey && event.shiftKey && ["3", "4", "5"].includes(event.key)) {
    return true;
  }
  if (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === "s") {
    return true;
  }
  return false;
};

export function useScreenshotProtection(enabled) {
  const [blocked, setBlocked] = useState(false);
  const hideTimerRef = useRef(null);

  const hideBlock = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    setBlocked(false);
  }, []);

  const showBlock = useCallback(
    (durationMs = OVERLAY_DURATION_MS) => {
      setBlocked(true);
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
      if (durationMs > 0) {
        hideTimerRef.current = setTimeout(() => {
          if (document.visibilityState === "visible" && document.hasFocus()) {
            setBlocked(false);
          }
          hideTimerRef.current = null;
        }, durationMs);
      }
    },
    []
  );

  useEffect(() => {
    if (!enabled) {
      hideBlock();
      return undefined;
    }

    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        if (hideTimerRef.current) {
          clearTimeout(hideTimerRef.current);
          hideTimerRef.current = null;
        }
        setBlocked(true);
        return;
      }
      if (document.hasFocus()) {
        hideBlock();
      }
    };

    const handleBlur = () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
      setBlocked(true);
    };

    const handleFocus = () => {
      if (document.visibilityState === "visible") {
        hideBlock();
      }
    };

    const handleKeyDown = (event) => {
      if (!isScreenshotShortcut(event)) return;
      event.preventDefault();
      showBlock(OVERLAY_DURATION_MS);
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText("").catch(() => {});
      }
    };

    const handleCopy = (event) => {
      event.preventDefault();
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("blur", handleBlur);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("copy", handleCopy, true);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("copy", handleCopy, true);
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
    };
  }, [enabled, hideBlock, showBlock]);

  return blocked;
}
